import React, { useState, useEffect, useMemo } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import {
  X, Check, ArrowRight, User, Mail, Building, Globe, Phone,
  MessageSquare, AlertCircle, Zap, Info, Database, Calendar, BarChart3, Sparkles, SlidersHorizontal,
} from 'lucide-react';
import { getPack, SHELL_COPY } from './content';

/* One modal, four content packs. The area comes from the active tab, so the
   form opens on the real question instead of first asking which of four mixed
   areas you meant. Everything area-specific lives in content.js; this file
   only knows how to render a field by its `kind` and how to post the result. */

const ICONS = {
  zap: Zap, alert: AlertCircle, calendar: Calendar, chart: BarChart3,
  database: Database, sparkles: Sparkles, message: MessageSquare,
  user: User, mail: Mail, building: Building, globe: Globe, phone: Phone,
};

const TEXT_KINDS = ['text', 'email', 'tel'];

const LabelWithTooltip = ({ label, icon: Icon, color, tooltip }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-text-muted flex items-center gap-2">
        {Icon && <Icon size={14} style={{ color }} />} {label}
      </label>
      {tooltip && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            className="p-1 -m-1 transition-all duration-300"
            aria-label="Info"
          >
            <Info size={14} className={`text-text-muted transition-opacity ${isOpen ? 'opacity-100' : 'opacity-40'}`} />
          </button>
          <AnimatePresence>
            {isOpen && (
              <Motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className="absolute top-full right-0 mt-2 w-52 p-3 bg-surface-color border border-border-color rounded-lg text-[10px] font-medium leading-relaxed text-text-muted z-[240] shadow-2xl pointer-events-none"
              >
                {tooltip}
              </Motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

const FloatingInput = ({ label, icon: Icon, value = '', name, type = 'text', onChange, placeholder, color, isTextArea = false }) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value && value.length > 0;
  const base = `w-full bg-surface-color/50 border border-border-color rounded-lg px-4 ${isTextArea ? 'pt-7 pb-3' : 'pt-6 md:pt-7 pb-1.5 md:pb-2'} !text-[16px] focus:outline-none focus:border-text-color/20 transition-all duration-300`;

  return (
    <div className="relative group">
      <div className={`absolute left-4 pointer-events-none transition-all duration-300 group-focus-within:opacity-100 opacity-40 ${isTextArea ? 'top-5' : 'top-1/2 -translate-y-1/2'}`}>
        {Icon && <Icon size={16} style={{ color: isFocused ? color : 'currentColor' }} />}
      </div>
      <div
        className={`absolute left-11 right-4 truncate transition-all duration-300 pointer-events-none uppercase tracking-widest font-black ${isFocused || hasValue ? 'top-1.5 md:top-2 text-[8px] opacity-100' : (isTextArea ? 'top-5 text-[10px] opacity-40' : 'top-1/2 -translate-y-1/2 text-[10px] opacity-40')}`}
        style={{ color: (isFocused || hasValue) ? color : 'inherit' }}
      >
        {label}
      </div>
      {isTextArea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={isFocused ? placeholder : ''}
          className={`${base} pl-11 h-32 resize-none`}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={isFocused ? placeholder : ''}
          className={`${base} pl-11`}
        />
      )}
    </div>
  );
};

// Text color on a `color` fill: the Design accent turns yellow in dark mode,
// where white labels would vanish; every other accent carries white.
const inkOn = (color) => (color === 'var(--design-primary)' ? 'var(--design-primary-ink)' : '#ffffff');

const Pill = ({ label, active, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-[10px] md:text-xs font-black border transition-all duration-300 ${active ? '' : 'border-border-color text-text-muted hover:border-text-color/30'}`}
    style={{ backgroundColor: active ? color : 'transparent', borderColor: active ? color : '', ...(active && { color: inkOn(color) }) }}
  >
    {label}
  </button>
);

/* Which options a field offers right now. `optionsBy` swaps the whole set based
   on another answer (concierge industry); `filterBy` narrows a fixed set to
   what the earlier answers make plausible, and falls back to the full set when
   nothing matches, so a field is never empty for lack of a mapping. */
const resolveOptions = (field, values) => {
  let options = field.options || [];

  if (field.optionsBy) {
    const key = values[field.optionsBy.field];
    if (!key) return [];
    options = field.optionsBy.map[key] || field.optionsBy.map[field.optionsBy.fallback] || [];
  }

  if (field.filterBy) {
    const raw = values[field.filterBy.field];
    const picked = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const allowed = new Set();
    picked.forEach((id) => (field.filterBy.map[id] || []).forEach((v) => allowed.add(v)));
    if (allowed.size > 0) options = options.filter((o) => allowed.has(o.id));
  }

  return options;
};

const emptyValue = (field) => (field.kind === 'multi' ? [] : '');

const InquiryModal = ({ isOpen, onClose, area = 'design', lang = 'de', color, onOpenConfigurator }) => {
  const pack = getPack(area);
  const shell = SHELL_COPY[lang] || SHELL_COPY.de;
  const L = (node) => (node && typeof node === 'object' ? (node[lang] ?? node.de) : node);

  const blankForm = useMemo(() => {
    const next = {};
    pack.steps.forEach((step) => step.fields.forEach((f) => { next[f.id] = emptyValue(f); }));
    return next;
  }, [pack]);

  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState(blankForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const lastStep = pack.steps.length - 1;

  useEffect(() => {
    if (!isOpen) return undefined;

    setStepIndex(0);
    setValues(blankForm);
    setErrors({});
    setIsSubmitting(false);
    setIsConfirmed(false);
    setSubmitError('');
    document.body.style.overflow = 'hidden';

    const onEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEscape);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', onEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, area]);

  /* Clearing a field that other fields key off of would otherwise leave stale
     answers behind — a concierge task list from the previously picked industry,
     say. Dependants are wiped whenever their source changes. */
  const setValue = (field, value) => {
    setValues((current) => {
      const next = { ...current, [field.id]: value };
      pack.steps.forEach((step) => step.fields.forEach((other) => {
        const source = other.optionsBy?.field || other.filterBy?.field;
        if (source === field.id) next[other.id] = emptyValue(other);
      }));
      return next;
    });
    setErrors((current) => ({ ...current, [field.id]: '' }));
  };

  const toggleValue = (field, id) => {
    const current = values[field.id] || [];
    setValue(field, current.includes(id) ? current.filter((v) => v !== id) : [...current, id]);
  };

  const stepErrors = (index) => {
    const next = {};
    pack.steps[index].fields.forEach((field) => {
      if (!field.required) return;
      const value = values[field.id];
      if (field.kind === 'multi' && (!value || value.length === 0)) next[field.id] = shell.required;
      if (field.kind === 'single' && !value) next[field.id] = shell.required;
      if (TEXT_KINDS.includes(field.kind)) {
        if (!String(value || '').trim()) next[field.id] = shell.required;
        else if (field.kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) next[field.id] = shell.invalidEmail;
      }
    });
    return next;
  };

  const isStepInvalid = Object.keys(stepErrors(stepIndex)).length > 0;

  const handleNext = () => {
    const found = stepErrors(stepIndex);
    setErrors(found);
    if (Object.keys(found).length === 0) setStepIndex((i) => Math.min(i + 1, lastStep));
  };

  /* Answers are stored as ids so the maps in content.js stay language-neutral;
     the mail wants readable copy, so they are resolved back here. */
  const labelsFor = (field) => {
    const options = resolveOptions(field, values);
    const raw = values[field.id];
    const picked = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    return picked.map((id) => L(options.find((o) => o.id === id)) || id);
  };

  const buildPayload = () => {
    const contact = {};
    const extraFields = [];
    const slots = { goals: [], painpoints: [], industry: '', timeline: '', budget: '' };

    // Slots the mail template already renders by name; every other answer
    // travels as a label/value pair so no pack-specific field gets dropped.
    const CONTACT_SLOTS = ['name', 'email', 'company', 'website', 'phone', 'details'];
    const mailLabel = (field) => field.mailLabel || L(field).toUpperCase();

    pack.steps.forEach((step) => step.fields.forEach((field) => {
      if (TEXT_KINDS.includes(field.kind) || field.kind === 'textarea') {
        const text = String(values[field.id] || '').trim();
        if (CONTACT_SLOTS.includes(field.id)) contact[field.id] = text;
        else if (text) extraFields.push({ label: mailLabel(field), value: text });
        return;
      }
      const labels = labelsFor(field);
      if (labels.length === 0) return;

      if (field.role === 'goals') slots.goals = labels;
      else if (field.role === 'industry') slots.industry = labels[0];
      else if (field.id === 'painpoints') slots.painpoints = labels;
      else if (field.id === 'timeline') slots.timeline = labels[0];
      else if (field.id === 'budget') slots.budget = labels[0];
      else extraFields.push({ label: mailLabel(field), value: labels.join(', ') });
    }));

    return {
      lang,
      area,
      type: pack.type,
      focus: `${L(pack.subject)} — ${L(pack.eyebrow)}`,
      name: contact.name || '-',
      email: contact.email || '',
      company: contact.company || '-',
      website: contact.website || '-',
      phone: contact.phone || '-',
      industry: slots.industry || '-',
      goals: slots.goals,
      painpoints: slots.painpoints,
      timeline: slots.timeline || '-',
      budget: slots.budget || '-',
      details: contact.details || '-',
      extraFields,
      origin: window.location.origin,
    };
  };

  const handleSubmit = () => {
    const found = stepErrors(lastStep);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSubmitting(true);
    setSubmitError('');

    fetch('/api/booking', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(buildPayload()),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === 'false') throw new Error(data.message || 'Request failed');
        setIsConfirmed(true);
      })
      .catch(() => {
        if (window.location.protocol === 'file:') { setIsConfirmed(true); return; }
        setSubmitError(shell.error);
      })
      .finally(() => setIsSubmitting(false));
  };

  if (!isOpen) return null;

  const step = pack.steps[stepIndex];
  const textFields = step.fields.filter((f) => TEXT_KINDS.includes(f.kind));
  const otherFields = step.fields.filter((f) => !TEXT_KINDS.includes(f.kind));

  const renderChoice = (field) => {
    const options = resolveOptions(field, values);
    const selected = values[field.id];
    return (
      <div key={field.id} className="space-y-4">
        <LabelWithTooltip label={L(field)} icon={ICONS[field.icon]} color={color} tooltip={L(field.tooltip)} />
        {options.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {options.map((opt) => (
              <Pill
                key={opt.id}
                label={L(opt)}
                color={color}
                active={field.kind === 'multi' ? (selected || []).includes(opt.id) : selected === opt.id}
                onClick={() => (field.kind === 'multi' ? toggleValue(field, opt.id) : setValue(field, opt.id))}
              />
            ))}
          </div>
        ) : (
          <p className="text-[10px] uppercase font-black tracking-widest text-text-muted/60 italic">
            {L(field.emptyHint) || ''}
          </p>
        )}
        {errors[field.id] && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors[field.id]}</p>}
      </div>
    );
  };

  return (
    <AnimatePresence>
      <Motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[220] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-hidden"
        onClick={onClose}
      >
        <Motion.div
          initial={{ y: 50, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 50, opacity: 0, scale: 0.95 }}
          className="w-full max-w-2xl max-h-[90vh] bg-bg-color border border-border-color rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {!isConfirmed && (
            <div className="relative p-6 md:p-8 pb-0 md:pb-0">
              <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-text-color/5 rounded-full transition-colors z-10" aria-label={shell.close}>
                <X size={20} />
              </button>

              <div className="flex items-center gap-3 mb-2">
                <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color }}>
                  {shell.step} {stepIndex + 1} / {pack.steps.length}
                </span>
                <div className="flex gap-1">
                  {pack.steps.map((s, i) => (
                    <div
                      key={L(s.label)}
                      className={`h-0.5 w-5 rounded-full transition-all duration-500 ${stepIndex >= i ? '' : 'bg-border-color'}`}
                      style={{ backgroundColor: stepIndex >= i ? color : '' }}
                    />
                  ))}
                </div>
              </div>
              <h4 className="text-xl md:text-2xl font-black tracking-tight mb-1">{L(pack.title)}</h4>
              <p className="text-[11px] md:text-xs text-text-muted font-light leading-relaxed pr-8">{L(pack.subtitle)}</p>
            </div>
          )}

          <div
            className={`${isConfirmed ? 'p-10' : 'p-6 md:p-8 pt-6 md:pt-8'} overflow-y-auto custom-scrollbar flex-1 scroll-smooth`}
            style={{ overscrollBehavior: 'contain' }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <AnimatePresence mode="wait">
              {isConfirmed ? (
                <Motion.div
                  key="success"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex flex-col items-center text-center py-8 relative"
                >
                  <button onClick={onClose} className="absolute -top-4 -right-4 p-2 hover:bg-text-color/5 rounded-full transition-colors z-10" aria-label={shell.close}>
                    <X size={20} />
                  </button>
                  <div
                    className="w-20 h-20 rounded-full aspect-square flex-shrink-0 flex items-center justify-center mb-8"
                    style={{ backgroundColor: `color-mix(in srgb, ${color} 20%, transparent)` }}
                  >
                    <Check size={48} style={{ color }} />
                  </div>
                  <h4 className="text-3xl md:text-5xl font-black mb-6 leading-none">{L(pack.confirmation.title)}</h4>
                  <p className="text-text-muted max-w-md mx-auto leading-relaxed text-sm md:text-base font-light">{L(pack.confirmation.text)}</p>
                </Motion.div>
              ) : (
                <Motion.div
                  key={`step-${stepIndex}`}
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -20, opacity: 0 }}
                  className="space-y-8"
                >
                  {otherFields.filter((f) => f.kind !== 'textarea').map(renderChoice)}

                  {textFields.length > 0 && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {textFields.map((field) => (
                          <div key={field.id} className="space-y-2">
                            <FloatingInput
                              label={L(field)}
                              icon={ICONS[field.icon]}
                              type={field.kind === 'email' ? 'email' : field.kind === 'tel' ? 'tel' : 'text'}
                              name={field.id}
                              value={values[field.id]}
                              placeholder={L(field.placeholder)}
                              onChange={(e) => setValue(field, e.target.value)}
                              color={color}
                            />
                            {errors[field.id] && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors[field.id]}</p>}
                          </div>
                        ))}
                      </div>
                      {step.note && (
                        <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted/70">{L(step.note)}</p>
                      )}
                    </>
                  )}

                  {otherFields.filter((f) => f.kind === 'textarea').map((field) => (
                    <FloatingInput
                      key={field.id}
                      label={L(field)}
                      icon={ICONS[field.icon]}
                      name={field.id}
                      isTextArea
                      value={values[field.id]}
                      placeholder={L(field.placeholder)}
                      onChange={(e) => setValue(field, e.target.value)}
                      color={color}
                    />
                  ))}

                  {/* Side door for anyone who would rather price the scope
                      themselves than hand it over — a banner, since as a line
                      of small print nobody found it. */}
                  {step.configuratorHint && onOpenConfigurator && (
                    <button
                      type="button"
                      onClick={() => { onClose(); onOpenConfigurator(); }}
                      className="group w-full flex items-center gap-4 text-left p-4 rounded-xl border transition-colors duration-300"
                      style={{
                        borderColor: `color-mix(in srgb, ${color} 35%, transparent)`,
                        backgroundColor: `color-mix(in srgb, ${color} 7%, transparent)`,
                      }}
                    >
                      <span
                        className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `color-mix(in srgb, ${color} 18%, transparent)` }}
                      >
                        <SlidersHorizontal size={18} style={{ color }} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-black tracking-wide" style={{ color }}>
                          {L(step.configuratorHint)}
                        </span>
                        <span className="block text-[11px] leading-relaxed text-text-muted font-light mt-0.5">
                          {L(step.configuratorHint.body)}
                        </span>
                      </span>
                      <span className="shrink-0 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest" style={{ color }}>
                        <span className="hidden sm:inline">{L(step.configuratorHint.link)}</span>
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </button>
                  )}
                </Motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="p-4 md:p-8 pt-4 md:pt-4 border-t border-border-color flex justify-between items-center gap-3 bg-bg-color">
            {!isConfirmed ? (
              <>
                <button
                  onClick={stepIndex === 0 ? onClose : () => setStepIndex((i) => i - 1)}
                  className="px-4 md:px-8 py-2.5 md:py-3 text-[10px] md:text-xs font-black uppercase tracking-widest border border-border-color rounded-lg hover:bg-text-color/5 transition-colors text-text-muted hover:text-text-color"
                >
                  {stepIndex === 0 ? shell.cancel : shell.back}
                </button>

                <div className="flex flex-col items-end gap-2">
                  {submitError && <span className="text-[10px] text-red-500 uppercase font-black text-right">{submitError}</span>}
                  <button
                    onClick={stepIndex === lastStep ? handleSubmit : handleNext}
                    disabled={isSubmitting || isStepInvalid}
                    className="group relative px-4 md:px-10 py-2.5 md:py-3 text-[10px] md:text-xs font-black uppercase tracking-widest rounded-lg overflow-hidden disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: color, color: inkOn(color) }}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {stepIndex === lastStep ? (isSubmitting ? shell.sending : L(pack.submit)) : shell.next}
                      {stepIndex < lastStep && <ArrowRight size={14} />}
                    </span>
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={onClose}
                className="w-full py-4 text-xs font-black uppercase tracking-widest rounded-lg transition-colors"
                style={{ backgroundColor: color, color: inkOn(color) }}
              >
                {shell.close}
              </button>
            )}
          </div>
        </Motion.div>
      </Motion.div>
    </AnimatePresence>
  );
};

export default InquiryModal;
